
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { findLyricsByAudioFile, getSongFilename, LYRICS_CHANGED_EVENT } from '@/utils/lyricsUtils';

export function VisualizerLyrics({ currentSong, currentTime, isPlaying }) {
  const [lyrics, setLyrics] = useState(null);
  const [currentLyricIndex, setCurrentLyricIndex] = useState(-1);

  const loadLyricsForCurrentSong = useCallback(() => {
    if (!currentSong) {
      setLyrics(null);
      setCurrentLyricIndex(-1);
      return;
    }
    const songLyrics = findLyricsByAudioFile(currentSong);
    setLyrics(songLyrics); 
    setCurrentLyricIndex(-1); 
    console.log(`VisualizerLyrics: Loaded lyrics for ${getSongFilename(currentSong)} - Found: ${!!songLyrics}`);
  }, [currentSong]);

  useEffect(() => {
    loadLyricsForCurrentSong();
  }, [currentSong, loadLyricsForCurrentSong]);

  useEffect(() => {
    const handleLyricsDataChange = (event) => {
      console.log('VisualizerLyrics: Custom lyrics event received:', event.detail);
      // Check if the change is relevant to the current song or a global purge
      if (event.detail?.action === 'purgedAll' || 
          (event.detail?.songKey && event.detail.songKey === getSongFilename(currentSong)) ||
          !event.detail?.songKey ) { // If no specific song key, assume it might be relevant
        loadLyricsForCurrentSong();
      }
    };

    window.addEventListener(LYRICS_CHANGED_EVENT, handleLyricsDataChange);
    // Still keep storage event as a fallback
    const handleStorageChange = (event) => {
      if (event.key === 'sonicGrooveLyrics' || event.key === null) {
        console.log('VisualizerLyrics: Detected localStorage (storage event), reloading lyrics for current song.');
        loadLyricsForCurrentSong();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener(LYRICS_CHANGED_EVENT, handleLyricsDataChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [loadLyricsForCurrentSong, currentSong]);


  useEffect(() => {
    if (!lyrics || !lyrics.lyrics || !Array.isArray(lyrics.lyrics) || lyrics.lyrics.length === 0) {
      if (currentLyricIndex !== -1) {
        setCurrentLyricIndex(-1);
      }
      return;
    }

    if (currentTime === undefined || currentTime === null || isNaN(currentTime)) {
      if (currentLyricIndex !== -1) {
        setCurrentLyricIndex(-1);
      }
      return;
    }

    let foundIndex = -1;
    
    for (let i = 0; i < lyrics.lyrics.length; i++) {
      const lyric = lyrics.lyrics[i];
      
      if (typeof lyric.startTime !== 'number' || typeof lyric.endTime !== 'number') {
        continue;
      }
      
      if (currentTime >= lyric.startTime && currentTime <= lyric.endTime) {
        foundIndex = i;
        break;
      }
    }

    if (foundIndex !== currentLyricIndex) {
      setCurrentLyricIndex(foundIndex);
    }
  }, [lyrics, currentTime, currentLyricIndex]);


  if (!currentSong || !lyrics || !lyrics.lyrics || !Array.isArray(lyrics.lyrics) || lyrics.lyrics.length === 0) {
    return null;
  }

  const currentLyric = currentLyricIndex >= 0 ? lyrics.lyrics[currentLyricIndex] : null;

  if (!currentLyric) {
    return null;
  }

  return (
    <div className="flex items-center justify-center w-full">
      <AnimatePresence mode="wait">
        <motion.div
          key={`lyric-${currentLyricIndex}-${currentLyric.text}`}
          initial={{ opacity: 0, y: -20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.9 }}
          transition={{ 
            duration: 0.4,
            ease: "easeOut"
          }}
          className="text-center px-6 py-3 bg-black/30 backdrop-blur-md rounded-lg border border-white/20"
        >
          <motion.p 
            className="text-2xl font-bold text-white leading-relaxed drop-shadow-2xl"
            initial={{ filter: "blur(4px)" }}
            animate={{ filter: "blur(0px)" }}
            transition={{ duration: 0.3, delay: 0.1 }}
            style={{
              textShadow: '0 0 20px rgba(255, 255, 255, 0.5), 0 0 40px rgba(59, 130, 246, 0.3)',
              fontWeight: '700',
              letterSpacing: '0.02em'
            }}
          >
            {currentLyric.text}
          </motion.p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
